import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Modal,
  Upload,
  Alert,
  message,
  Table,
  Tag,
  Tooltip,
  Button,
} from "antd";
import {
  InboxOutlined,
  QuestionCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import readXlsxFile from "read-excel-file";
import { validateBulkCreateUser, bulkCreateUser } from "apis/user";
import { ScreenSize } from "utils/constants";
import Locale from "react-intl-universal";

const BulkCreateUserModal = (props) => {
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [uploadedFile, setUploadedFile] = useState([]);

  const handleUploadedTemplate = async (value) => {
    /* 
Flow
1. Check is file valid
2. Parse excel file
3. Check parsed file format
4. Compose data
5. Check value
 */
    setUploadedFile([value.file]);
    var output = await parseExcel(value.file);

    const isSchemaValid = await validateFormat(output);
    if (isSchemaValid.error === true) {
      message.error(isSchemaValid.message);
      setUploadedFile([]);
      return;
    }

    var newData = [];
    for (let i = 0; i < output.rows.length; i++) {
      var item = output.rows[i];
      let errorList = [];
      // Check email duplicate data
      const emailDuplicate = output.rows.filter((x) => x.email === item.email);
      if (emailDuplicate.length > 1) {
        errorList.push({
          error: "Duplicate",
          column: "Email",
        });
      }
      // Check username duplicate
      const usernameDuplicate = output.rows.filter(
        (x) => x.username === item.username
      );
      if (usernameDuplicate.length > 1) {
        errorList.push({
          error: "Duplicate",
          column: "Username",
        });
      }

      errorList = [
        ...errorList,
        ...output.errors.filter((err) => err.row === i + 2),
      ];

      newData.push({
        ...item,
        haveError: errorList,
      });
    }
    console.log(newData, output);

    setLoading(true);
    const apiValidatedData = await validateUsername(newData);
    setTableData(apiValidatedData);
    setShowUpload(false);
    setLoading(false);
  };

  const parseExcel = (file) => {
    return new Promise((resolve, reject) => {
      readXlsxFile(file, {
        schema: {
          "First Name": {
            prop: "firstName",
            type: String,
            required: true,
          },
          "Last Name": {
            prop: "lastName",
            type: String,
            required: true,
          },
          "Country Code": {
            prop: "countryCode",
            required: true,
            type: (value) => {
              const countryCode = [60, 65, 62];
              const output = countryCode.filter((x) => x === value);
              if (output.length === 0) {
                throw new Error("invalid");
              }
              return value;
            },
          },
          "Contact Number": {
            prop: "contact",
            type: Number,
          },
          Email: {
            prop: "email",
            required: true,
            type: (value) => {
              const regex = RegExp(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/);
              const output = regex.test(value);
              if (!output) {
                throw new Error("invalid");
              }
              return value;
            },
          },
          "Company Name": {
            prop: "companyName",
            type: String,
            required: true,
          },
          Username: {
            prop: "username",
            type: String,
            required: true,
          },
          Role: {
            prop: "role",
            required: true,
            type: (value) => {
              const role = [
                "Free",
                "Agent Entry",
                "Agent Full",
                "Leader",
                "Contributor",
                "Agent Full and Contributor",
              ];
              const output = role.filter((x) => x === value);
              if (output.length === 0) {
                throw new Error("invalid");
              }
              return value;
            },
          },
          Country: {
            prop: "country",
            required: true,
            type: (value) => {
              const country = ["Malaysia", "Singapore", "Indonesia"];
              const output = country.filter((x) => x === value);
              if (output.length === 0) {
                throw new Error("invalid");
              }
              return value;
            },
          },
        },
      })
        .then((output) => {
          resolve(output);
        })
        .catch((err) => {
          message.error(Locale.get("Users.bulkCreateModal.invalidExcelFile"));
          reject(err);
        });
    });
  };

  const validateFormat = (output) => {
    if (output.rows.length === 0) {
      setUploadedFile([]);
      return {
        error: true,
        message: "Invalid schema",
      };
    }
    if (output.errors.length > 0) {
      message.error(Locale.get("Users.bulkCreateModal.containError"));
      return {
        error: false,
        message: "Contain error please fix and try again",
      };
    }
    return {
      error: false,
    };
  };

  const validateUsername = async (userList) => {
    const res = await validateBulkCreateUser(userList);
    console.log(res);
    return res;
  };

  const handleBulkCreateUser = async () => {
    if (tableData.length === 0) {
      return message.error(Locale.get("Users.bulkCreateModal.noDataSubmit"));
    }

    const triggerError = await tableData.filter((x) => x.haveError.length > 0);
    if (triggerError.length > 0) {
      return message.error(
        Locale.get("Users.bulkCreateModal.fixErrorToProceed")
      );
    }
    setLoading(true);
    const res = await bulkCreateUser(tableData);
    console.log(res);

    setShowUpload(true);
    setUploadedFile([]);
    setTableData([]);
    props.onCancel();

    Modal.success({
      title: Locale.get("Users.bulkCreateModal.bulkCreateSuccess"),
      onOk: () => window.location.reload(),
    });
  };

  const columns = [
    {
      title: "Status",
      sorter: (a, b) => a.haveError.length - b.haveError.length,
      render: (index) => {
        if (index.haveError.length === 0) {
          return <Tag color="green">Accepted</Tag>;
        } else {
          return (
            <Tooltip
              title={
                <Row>
                  <Col span={24} style={{ fontWeight: 600 }}>
                    Error List :
                  </Col>
                  {index.haveError.map((value, index) => (
                    <Col span={24}>
                      {index + 1}. {value.column} {value.error}
                    </Col>
                  ))}
                </Row>
              }
            >
              <Tag color="red">
                <QuestionCircleOutlined /> Error
              </Tag>
            </Tooltip>
          );
        }
      },
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Country Code",
      dataIndex: "countryCode",
      key: "countryCode",
    },
    {
      title: "Contact",
      dataIndex: "contact",
      key: "contact",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
  ];

  const previewTable = (
    <div>
      <Row>
        <Alert
          message={
            <span>{Locale.get("Users.bulkCreateModal.informationNote")}</span>
          }
          type="info"
          style={{
            borderColor: "#91d5ff",
            backgroundColor: "#e6f7ff",
            marginBottom: 16,
          }}
        />
      </Row>
      <Table
        size="small"
        columns={columns}
        rowKey={(record) => record.email}
        dataSource={tableData}
        scroll={{ x: window.innerWidth < ScreenSize.xs ? "95%" : 1200 }}
        footer={() => (
          <Button
            danger
            size="small"
            onClick={() => {
              setUploadedFile([]);
              setTableData([]);
              setShowUpload(true);
            }}
          >
            <CloseOutlined />{" "}
            {Locale.get("Users.bulkCreateModal.resetResubmit")}
          </Button>
        )}
      />
    </div>
  );

  return (
    <Modal
      visible={props.visible}
      onCancel={props.onCancel}
      title={Locale.get("Users.bulkCreateUsers")}
      onOk={handleBulkCreateUser}
    >
      <Alert
        message={
          <span>
            {Locale.get("Users.bulkCreateModal.modalAlertMsg_0")}:{" "}
            <a
              href={"/BulkCreateUserTemplate.xlsx"}
              style={{
                textDecoration: "underline",
              }}
            >
              {Locale.get("Users.bulkCreateModal.modalAlertMsg_1")}
            </a>
          </span>
        }
        type="info"
        style={{
          borderColor: "#91d5ff",
          backgroundColor: "#e6f7ff",
          marginBottom: 16,
        }}
      />

      {showUpload ? (
        <Upload.Dragger
          beforeUpload={() => false}
          onChange={handleUploadedTemplate}
          fileList={uploadedFile}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {Locale.get("Users.bulkCreateModal.uploadDraggerTag_0")}
          </p>
          <p className="ant-upload-hint">
            {Locale.get("Users.bulkCreateModal.uploadDraggerTag_1")}
          </p>
        </Upload.Dragger>
      ) : (
        previewTable
      )}
    </Modal>
  );
};

export default BulkCreateUserModal;
